// ──────────────────────────────
// PricingSection.jsx (actualizado)
// ──────────────────────────────
import React, { useEffect, useState, useCallback, Fragment } from "react";
import { fetchPublicProducts } from "../api/api_functions";
import "./PricingSection.css";
// import redRoutinesIcon from "../assets/images/shapes/routines_icon_red.png";
// import redDietsIcon from "../assets/images/shapes/diets_icon_red.png";
import logoZ        from "../assets/images/logos/logoZ.png";

const APP_URL = process.env.REACT_APP_APP_URL || "https://app.zasfit.io";

/* util para trocear precio */
const usePriceParts = () =>
  useCallback((value, locale = "es-ES") => {
    const parts = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).formatToParts(value);

    let integer = "", dec = ",", frac = "";
    parts.forEach((p) => {
      if (p.type === "integer")  integer = p.value;
      if (p.type === "decimal")  dec     = p.value;
      if (p.type === "fraction") frac    = p.value;
    });
    return { integer, dec, frac };
  }, []);

/* 🪄 Tarjeta placeholder — misma estructura, sin datos */
const SkeletonCard = ({ i }) => (
  <div className="col d-flex" key={`sk-${i}`}>
    <article className="pricing-item placeholder w-100 h-100 p-4 text-center d-flex flex-column align-items-center">
      {/* logo */}
      <span className="plan-logo-wrap mb-0">
        <span className="skeleton skeleton-logo" />
      </span>

      {/* título + descripción */}
      <div className="plan-title mb-0 skeleton skeleton-title" aria-hidden="true" />
      <p className="plan-description skeleton skeleton-desc" />

      {/* precio */}
      <div className="price mb-5 w-100 d-flex justify-content-center">
        <span className="skeleton skeleton-price" />
      </div>

      {/* CTA */}
      <span className="plan-btn skeleton skeleton-btn mb-0" />

      {/* features (4 líneas) */}
      <ul className="plan-features list-style-one">
        {Array.from({ length: 4 }).map((_, j) => (
          <li key={j}>
            <span className="skeleton skeleton-feature" />
          </li>
        ))}
      </ul>
    </article>
  </div>
);

export default function PricingSection() {
  const [plans, setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const format = usePriceParts();

  /* fetch público */
  useEffect(() => {
    (async () => {
      try {
        const data   = await fetchPublicProducts();
        const sorted = data
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.unit_amount - b.unit_amount);
        setPlans(sorted);
      } catch (e) {
        console.error(e);
        setError("No pudimos cargar los planes. Intenta más tarde.");
      } finally {
        setLoading(false);
      }
    })();
  }, [format]);

  /* cuántos placeholders mostrar (5 → caben a ≥ 1200 px)*/
  const skeletonCount = 5;

  useEffect(() => {
    if (!loading && !error) {
      // esperamos al repintado para que los nodos existan
      requestAnimationFrame(() => {
        if (window.wow) {
          window.wow.sync();          // registra los nuevos `.wow`
          window.wow.scrollHandler(); // fuerza comprobación por si ya están a la vista
        }
      });
    }
  }, [loading, error]);

  return (
    <section id="pricing" className="pricing-section rel z-1 bg-gray pt-50 pb-300">
      <div className="container-fluid px-4">
        <div className="section-title text-center text-white mb-45">
          <span className="sub-title sub-title-with-margin-bottom d-inline-block mb-10">Precios</span>
          <h2 className="section-title text-white mb-0">
            Elige tu Plan de Suscripción
          </h2>
        </div>

        {/* ─── grid con planes o placeholders ─── */}
        <div className="
            pricing-grid row
            row-cols-1 row-cols-lg-3 row-cols-xl-5 row-cols-xxl-5
            g-7 justify-content-center">

          {loading ? (
            /* placeholders */
            <Fragment>
              {Array.from({ length: skeletonCount }).map((_, i) => (
                <SkeletonCard i={i} key={i} />
              ))}
            </Fragment>
          ) : error ? (
            /* error */
            <p className="text-danger text-center w-100">{error}</p>
          ) : (
            /* planes reales */
            plans.map((plan, idx) => {
              const { integer, dec, frac } = format(plan.unit_amount / 100);
              const cta = plan.freeTrial
                ? plan.freeTrialDays
                  ? `Prueba Gratis ${plan.freeTrialDays} Días`
                  : "Prueba Gratis"
                : "Empieza ahora";

              return (
                <div key={plan.id} className="col d-flex">
                  <article
                    className={
                      `pricing-item h-100 p-4 text-center d-flex flex-column align-items-center
                      ${!loading ? "wow animate__animated animate__fadeInUpShort" : ""}`
                    }
                    data-wow-delay={`${idx * 0.15}s`}
                  >
                    <span className="plan-logo-wrap mb-0">
                      <img src={logoZ} alt="logo" className="plan-logo lg" />
                    </span>

                    <h5 className="plan-title mb-0">{plan.name}</h5>
                    {plan.description && (
                      <p className="plan-description text-muted small mb-0">
                        {plan.description}
                      </p>
                    )}

                    <div className="price mb-5">
                      <span className="amount">
                        {integer}
                        <span className="decimals">{dec}{frac}</span>€
                      </span>
                      <span className="interval">/mes</span>
                    </div>

                    <a href={`${APP_URL}/auth/register`}
                       className={`plan-btn ${plan.freeTrial ? "free-trial-btn" : "regular-btn"} mb-0`}>
                      {cta}
                    </a>

                    {plan.features?.length > 0 && (
                      <ul className="plan-features list-style-one">
                        {plan.features.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    )}
                  </article>
                </div>
              );
            })
          )}
        </div>
        <p
          className="
            prices-disclaimer
            wow                /* activa WOW.js                        */
            animate__animated  /* Animate.css                         */
            animate__fadeIn    /* solo opacidad, sin desplazamiento   */
          "
          data-wow-delay="0.3s"   /* opcional: retrasa la aparición   */
        >
          * Los precios mostrados no incluyen IVA. Este se aplicará en el proceso de pago.
        </p>

      </div>

      {/* decoraciones */}
      {/* <div className="section-alpha-text">Precios</div> */}
      {/* <img className="routines-shape" src={redRoutinesIcon} alt="" />
      <img className="diets-shape" src={redDietsIcon} alt="" /> */}
      <div className="left-circles" />
      <div className="right-circles" />
    </section>
  );
}
